import { makeVar } from '@apollo/client';

import { trackLogout } from '@/shared/api/services/activity-log';
import ls, { ACCESS_TOKEN_KEY, IDLE_KEY, REFRESH_TOKEN_KEY, RELAYING_PARTY_ID_KEY, ROLE_TYPE_KEY } from '@/shared/lib/util/localstorage';
import { clearSessionValues } from '@/shared/lib/util/sessionValue';
import { getPatientListNavigationState } from '@/shared/model/PatientListNavigationStore';

// Reactive vars initialized from localStorage on module load.
// Exported with a leading underscore — callers use useReactiveVar(_tokenVar) for reactive reads,
// or session.token for a one-shot synchronous read.
export const _tokenVar = makeVar<string | null>(ls.get(ACCESS_TOKEN_KEY));
export const _refreshTokenVar = makeVar<string | null>(ls.get(REFRESH_TOKEN_KEY));
export const _relayingPartyIdVar = makeVar<string | null>(ls.get(RELAYING_PARTY_ID_KEY));
export const _roleTypeVar = makeVar<string | null>(ls.get(ROLE_TYPE_KEY));

export const session = {
  get token(): string | null {
    return _tokenVar();
  },
  get refreshToken(): string | null {
    return _refreshTokenVar();
  },
  get relayingPartyId(): string | null {
    return _relayingPartyIdVar();
  },
  get roleType(): string | null {
    return _roleTypeVar();
  },
  get isAuthenticated(): boolean {
    return !!_tokenVar();
  },

  setToken(userToken: string): void {
    _tokenVar(userToken);
    ls.set(ACCESS_TOKEN_KEY, userToken);
  },

  setTokenAndRefreshToken(userToken: string, userRefreshToken: string): void {
    ls.remove(IDLE_KEY);
    // A new sign-in or pharmacy switch must not inherit the previous session's patient list filters.
    clearSessionValues();
    getPatientListNavigationState().clearSession();
    _tokenVar(userToken);
    _refreshTokenVar(userRefreshToken);
    ls.multiSet({
      [ACCESS_TOKEN_KEY]: userToken,
      [REFRESH_TOKEN_KEY]: userRefreshToken
    });
  },

  setRelayingPartyId(id: string): void {
    _relayingPartyIdVar(id);
    ls.set(RELAYING_PARTY_ID_KEY, id);
  },

  setRoleType(roleType: string): void {
    _roleTypeVar(roleType);
    ls.set(ROLE_TYPE_KEY, roleType);
  },

  getIsIdle(): string | null {
    return ls.get(IDLE_KEY);
  },

  // Marks idle timestamp (if timedOut) and asynchronously tracks the logout event.
  // handleLogout in useToken orchestrates the synchronous teardown (clearCache + ls.clear + navigate).
  logout(timedOut = false): void {
    if (timedOut) {
      ls.set(IDLE_KEY, new Date().toISOString());
    }
    _roleTypeVar(null);
    ls.remove(ROLE_TYPE_KEY);

    // Cleared synchronously rather than when trackLogout settles. Deferring them meant a slow
    // logout request could null the vars *after* a subsequent login had already set them, leaving
    // the in-memory session empty while localStorage still held a valid token. Callers that read
    // different halves of that split then disagree about whether anyone is signed in, which is
    // what ping-pongs the router between /login and the app. authLink builds its header from
    // localStorage, so clearing these first does not un-authenticate the request below.
    _tokenVar(null);
    _refreshTokenVar(null);

    trackLogout({ timedOut }).catch(console.error);
  }
};
